import { logWarning } from "web_myplann/Infrastructure/sentry";
import {
	type BiometryStatusRequest,
	type BiometryStatusResponse,
	type ChangeDashboardColorRequest,
	type ChangeDashboardColorResponse,
	type ChangeProfileImageRequest,
	type ChangeProfileImageResponse,
	type ConsoleLogRequest,
	type ConsoleLogResponse,
	type HandleDocumentPayload,
	type HandleDocumentRequest,
	type HandleDocumentResponse,
	type InitRequest,
	type InitResponse,
	type Language,
	type LogOutRequest,
	type LogOutResponse,
	type NavigateBackRequest,
	type NavigateBackResponse,
	type NavigateChatRequest,
	type NavigateChatResponse,
	type NavigationRequest,
	type NavigationResponse,
	NavigationTypes,
	type OpenExternalUrlCookie,
	type OpenExternalUrlRequest,
	type OpenExternalUrlResponse,
	type PickAttachmentFileRequest,
	type PickAttachmentFileResponse,
	type PickAttachmentGalleryRequest,
	type PickAttachmentGalleryResponse,
	type PickAttachmentPhotoRequest,
	type PickAttachmentPhotoResponse,
	type PilotTab,
	type ProcessingOverlayRequest,
	type ProcessingOverlayResponse,
	type RemoveAttachmentRequest,
	type RemoveAttachmentResponse,
	type RemoveUserRequest,
	type RemoveUserResponse,
	type RpcMessage,
	type SaveAttachmentRequest,
	type SaveAttachmentResponse,
	type SessionExpiredRequest,
	type SessionExpiredResponse,
	type SetActiveTabRequest,
	type SetActiveTabResponse,
	type SetChatUnreadCountRequest,
	type SetChatUnreadCountResponse,
	type SetStatusBarOverlayRequest,
	type SetStatusBarOverlayResponse,
	type ShowNavbarRequest,
	type ShowNavbarResponse,
	type SwitchDarkMode,
	type SwitchLanguage,
	type WebViewMessage,
	WebViewMessageTypes,
} from "./WebViewModel";

export type { RpcMessage };
// Re-export so existing `import { NavigationTypes } from "web_myplann/Utils/device"`
// callers keep working without churn. New code should import from WebViewModel directly.
export { NavigationTypes };

export const isAndroidApp =
	!!window.ReactNativeWebView && /android/i.test(navigator.userAgent);

// TODO: fix by bundling,
// this is just temporary solution as it is used only in MyPlann
// and in other apps it's only poluting the console
const rootElement = document.getElementById("rootmydock2");
const dataModule =
	rootElement?.getAttribute("data-module") ?? import.meta.env.VITE_MODULE;

dataModule === "MyPlann" &&
	window.addEventListener("message", (evt: MessageEvent<string>) => {
		if (typeof evt.data !== "string") {
			return;
		}

		let parsed: unknown;
		try {
			parsed = JSON.parse(evt.data);
		} catch (_) {
			console.error("Invalid JSON from native: ", evt.data);
			if (typeof evt.data === "string" && evt.data.startsWith("{")) {
				logWarning("Invalid JSON from native", {
					data_length: evt.data.length,
				});
			}
			return;
		}

		if (
			typeof parsed === "object" &&
			parsed !== null &&
			"messageId" in parsed &&
			typeof (parsed as any).messageId === "string"
		) {
			const rpc = parsed as RpcMessage<WebViewMessage>;
			const entry = pending.get(rpc.messageId);
			if (!entry) return;
			clearTimeout(entry.timeoutId);
			pending.delete(rpc.messageId);
			entry.resolve(rpc);
		}
	});

interface PendingRequest<T extends WebViewMessage> {
	resolve: (msg: RpcMessage<T>) => void;
	reject: (error: Error) => void;
	timeoutId: number;
}

const pending = new Map<string, PendingRequest<WebViewMessage>>();

export function genMessageId(): string {
	return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export function postWithResponse(
	msg: InitRequest,
	timeoutMs?: number,
): Promise<RpcMessage<InitResponse>>;

export function postWithResponse(
	msg: NavigationRequest,
	timeoutMs?: number,
): Promise<RpcMessage<NavigationResponse>>;

export function postWithResponse(
	msg: NavigateBackRequest,
	timeoutMs?: number,
): Promise<RpcMessage<NavigateBackResponse>>;

export function postWithResponse(
	msg: NavigateChatRequest,
	timeoutMs?: number,
): Promise<RpcMessage<NavigateChatResponse>>;

export function postWithResponse(
	msg: PickAttachmentPhotoRequest,
	timeoutMs?: number,
): Promise<RpcMessage<PickAttachmentPhotoResponse>>;

export function postWithResponse(
	msg: PickAttachmentGalleryRequest,
	timeoutMs?: number,
): Promise<RpcMessage<PickAttachmentGalleryResponse>>;

export function postWithResponse(
	msg: PickAttachmentFileRequest,
	timeoutMs?: number,
): Promise<RpcMessage<PickAttachmentFileResponse>>;

export function postWithResponse(
	msg: SaveAttachmentRequest,
	timeoutMs?: number,
): Promise<RpcMessage<SaveAttachmentResponse>>;

export function postWithResponse(
	msg: RemoveAttachmentRequest,
	timeoutMs?: number,
): Promise<RpcMessage<RemoveAttachmentResponse>>;

export function postWithResponse(
	msg: HandleDocumentRequest,
	timeoutMs?: number,
): Promise<RpcMessage<HandleDocumentResponse>>;

export function postWithResponse(
	msg: ConsoleLogRequest,
	timeoutMs?: number,
): Promise<RpcMessage<ConsoleLogResponse>>;

export function postWithResponse(
	msg: BiometryStatusRequest,
	timeoutMs?: number,
): Promise<RpcMessage<BiometryStatusResponse>>;

export function postWithResponse(
	msg: SwitchDarkMode,
	timeoutMs?: number,
): Promise<RpcMessage<SwitchDarkMode>>;

export function postWithResponse(
	msg: SwitchLanguage,
	timeoutMs?: number,
): Promise<RpcMessage<SwitchLanguage>>;

export function postWithResponse(
	msg: LogOutRequest,
	timeoutMs?: number,
): Promise<RpcMessage<LogOutResponse>>;

export function postWithResponse(
	msg: SessionExpiredRequest,
	timeoutMs?: number,
): Promise<RpcMessage<SessionExpiredResponse>>;

export function postWithResponse(
	msg: ChangeProfileImageRequest,
	timeoutMs?: number,
): Promise<RpcMessage<ChangeProfileImageResponse>>;

export function postWithResponse(
	msg: RemoveUserRequest,
	timeoutMs?: number,
): Promise<RpcMessage<RemoveUserResponse>>;

export function postWithResponse(
	msg: ChangeDashboardColorRequest,
	timeoutMs?: number,
): Promise<RpcMessage<ChangeDashboardColorResponse>>;

export function postWithResponse(
	msg: OpenExternalUrlRequest,
	timeoutMs?: number,
): Promise<RpcMessage<OpenExternalUrlResponse>>;

export function postWithResponse(
	msg: ShowNavbarRequest,
	timeoutMs?: number,
): Promise<RpcMessage<ShowNavbarResponse>>;

export function postWithResponse(
	msg: ProcessingOverlayRequest,
	timeoutMs?: number,
): Promise<RpcMessage<ProcessingOverlayResponse>>;

export function postWithResponse(
	msg: SetStatusBarOverlayRequest,
	timeoutMs?: number,
): Promise<RpcMessage<SetStatusBarOverlayResponse>>;

export function postWithResponse(
	msg: SetActiveTabRequest,
	timeoutMs?: number,
): Promise<RpcMessage<SetActiveTabResponse>>;

export function postWithResponse(
	msg: SetChatUnreadCountRequest,
	timeoutMs?: number,
): Promise<RpcMessage<SetChatUnreadCountResponse>>;

export function postWithResponse(
	msg: WebViewMessage,
	timeoutMs = 5_000,
): Promise<RpcMessage<WebViewMessage>> {
	const messageId = genMessageId();
	const rpcReq = { ...msg, messageId } as RpcMessage<WebViewMessage>;

	return new Promise<RpcMessage<WebViewMessage>>((resolve, reject) => {
		const timeoutId = window.setTimeout(() => {
			pending.delete(messageId);
			reject(new Error(`Timeout waiting for ${msg.type} response`));
		}, timeoutMs);

		pending.set(messageId, { resolve, reject, timeoutId });
		window.ReactNativeWebView?.postMessage(JSON.stringify(rpcReq));
	});
}

export const device = {
	initializeWeb(): Promise<RpcMessage<InitResponse>> {
		const msg: InitRequest = {
			type: WebViewMessageTypes.INIT_REQUEST,
			payload: {},
		};
		return postWithResponse(msg);
	},

	navigationRequest(
		type: keyof typeof NavigationTypes,
		url?: string,
	): Promise<RpcMessage<NavigationResponse>> {
		const msg: NavigationRequest = {
			type: WebViewMessageTypes.NAVIGATION_REQUEST,
			payload: { type, url },
		};
		return postWithResponse(msg);
	},

	navigateBackRequest(): Promise<RpcMessage<NavigateBackResponse>> {
		const msg: NavigateBackRequest = {
			type: WebViewMessageTypes.NAVIGATE_BACK_REQUEST,
			payload: { navigateBack: true },
		};
		return postWithResponse(msg);
	},

	navigateChatRequest(
		conversationId: string,
		title: string,
		advisorId?: string,
		message?: string,
	): Promise<RpcMessage<NavigateChatResponse>> {
		const msg: NavigateChatRequest = {
			type: WebViewMessageTypes.NAVIGATE_CHAT_REQUEST,
			payload: { conversationId, title, advisorId, message },
		};
		return postWithResponse(msg);
	},

	async pickAttachmentPhoto(): Promise<
		RpcMessage<PickAttachmentPhotoResponse>
	> {
		const msg: PickAttachmentPhotoRequest = {
			type: WebViewMessageTypes.PICK_ATTACHMENT_PHOTO_REQUEST,
			payload: {},
		};
		return postWithResponse(msg, 120_000);
	},

	async pickAttachmentGallery(): Promise<
		RpcMessage<PickAttachmentGalleryResponse>
	> {
		const msg: PickAttachmentGalleryRequest = {
			type: WebViewMessageTypes.PICK_ATTACHMENT_GALLERY_REQUEST,
			payload: {},
		};
		return postWithResponse(msg, 120_000);
	},

	async pickAttachmentFile(): Promise<RpcMessage<PickAttachmentFileResponse>> {
		const msg: PickAttachmentFileRequest = {
			type: WebViewMessageTypes.PICK_ATTACHMENT_FILE_REQUEST,
			payload: {},
		};
		return postWithResponse(msg, 120_000);
	},

	async saveAttachment(file: {
		filename: string;
		base64: string;
	}): Promise<RpcMessage<SaveAttachmentResponse>> {
		const msg: SaveAttachmentRequest = {
			type: WebViewMessageTypes.SAVE_ATTACHMENT_REQUEST,
			payload: {
				file: {
					filename: file.filename,
					base64: file.base64,
				},
			},
		};
		return postWithResponse(msg, 120_000);
	},

	async removeAttachment(
		filename: string,
	): Promise<RpcMessage<RemoveAttachmentResponse>> {
		const msg: RemoveAttachmentRequest = {
			type: WebViewMessageTypes.REMOVE_ATTACHMENT_REQUEST,
			payload: { filename },
		};
		return postWithResponse(msg);
	},

	async handleDocument(
		payload: HandleDocumentPayload,
	): Promise<RpcMessage<HandleDocumentResponse>> {
		const msg: HandleDocumentRequest = {
			type: WebViewMessageTypes.HANDLE_DOCUMENT_REQUEST,
			payload: { ...payload },
		};
		return postWithResponse(msg, 120_000);
	},

	async consoleLog(
		message: Record<string, unknown>,
	): Promise<RpcMessage<ConsoleLogResponse>> {
		const msg: ConsoleLogRequest = {
			type: WebViewMessageTypes.CONSOLE_LOG_REQUEST,
			payload: { message },
		};
		return postWithResponse(msg);
	},

	async changeBiometryStatus(
		isEnabled: boolean,
	): Promise<RpcMessage<BiometryStatusResponse>> {
		const msg: BiometryStatusRequest = {
			type: WebViewMessageTypes.BIOMETRY_STATUS_REQUEST,
			payload: { isEnabled },
		};
		return postWithResponse(msg, 120_000);
	},

	async switchDarkMode(darkMode: boolean): Promise<RpcMessage<SwitchDarkMode>> {
		const msg: SwitchDarkMode = {
			type: WebViewMessageTypes.SWITCH_DARK_MODE,
			payload: { darkMode },
		};
		return postWithResponse(msg);
	},

	async switchLanguage(
		language: Language,
	): Promise<RpcMessage<SwitchLanguage>> {
		const msg: SwitchLanguage = {
			type: WebViewMessageTypes.SWITCH_LANGUAGE,
			payload: { language },
		};
		return postWithResponse(msg);
	},

	async logOut(): Promise<RpcMessage<LogOutResponse>> {
		const msg: LogOutRequest = {
			type: WebViewMessageTypes.LOG_OUT_REQUEST,
			payload: {},
		};
		return postWithResponse(msg);
	},

	async sessionExpired(): Promise<RpcMessage<SessionExpiredResponse>> {
		const msg: SessionExpiredRequest = {
			type: WebViewMessageTypes.SESSION_EXPIRED_REQUEST,
			payload: {},
		};
		return postWithResponse(msg);
	},

	async changeProfileImage(): Promise<RpcMessage<ChangeProfileImageResponse>> {
		const msg: ChangeProfileImageRequest = {
			type: WebViewMessageTypes.CHANGE_PROFILE_IMAGE_REQUEST,
			payload: {},
		};
		return postWithResponse(msg, 120_000);
	},

	async removeUser(): Promise<RpcMessage<RemoveUserResponse>> {
		const msg: RemoveUserRequest = {
			type: WebViewMessageTypes.REMOVE_USER_REQUEST,
			payload: {},
		};
		return postWithResponse(msg);
	},

	async changeDashboardColor(
		color: string,
	): Promise<RpcMessage<ChangeDashboardColorResponse>> {
		const msg: ChangeDashboardColorRequest = {
			type: WebViewMessageTypes.CHANGE_DASHBOARD_COLOR_REQUEST,
			payload: { color },
		};
		return postWithResponse(msg);
	},

	async showNavbar(show: boolean): Promise<RpcMessage<ShowNavbarResponse>> {
		const msg: ShowNavbarRequest = {
			type: WebViewMessageTypes.SHOW_NAVBAR_REQUEST,
			payload: { show },
		};
		return postWithResponse(msg);
	},

	async showProcessingOverlay(
		show: boolean,
	): Promise<RpcMessage<ProcessingOverlayResponse>> {
		const msg: ProcessingOverlayRequest = {
			type: WebViewMessageTypes.PROCESSING_OVERLAY_REQUEST,
			payload: { show },
		};
		return postWithResponse(msg);
	},

	async openExternalUrl(
		url: string,
		cookies?: OpenExternalUrlCookie[],
	): Promise<RpcMessage<OpenExternalUrlResponse>> {
		const msg: OpenExternalUrlRequest = {
			type: WebViewMessageTypes.OPEN_EXTERNAL_URL_REQUEST,
			payload: { url, cookies },
		};
		return postWithResponse(msg, 30_000);
	},

	async setActiveTab(tab: PilotTab): Promise<RpcMessage<SetActiveTabResponse>> {
		const msg: SetActiveTabRequest = {
			type: WebViewMessageTypes.SET_ACTIVE_TAB_REQUEST,
			payload: { tab },
		};
		return postWithResponse(msg);
	},

	async setChatUnreadCount(
		count: number,
	): Promise<RpcMessage<SetChatUnreadCountResponse>> {
		const msg: SetChatUnreadCountRequest = {
			type: WebViewMessageTypes.SET_CHAT_UNREAD_COUNT_REQUEST,
			payload: { count },
		};
		return postWithResponse(msg);
	},

	async setStatusBarOverlay(
		enabled: boolean,
		darkContent?: boolean,
	): Promise<RpcMessage<SetStatusBarOverlayResponse>> {
		const msg: SetStatusBarOverlayRequest = {
			type: WebViewMessageTypes.SET_STATUS_BAR_OVERLAY_REQUEST,
			payload: { enabled, darkContent },
		};
		return postWithResponse(msg);
	},
};

export const withProcessingOverlay = async <T>(action: () => Promise<T>) => {
	void device.showProcessingOverlay(true);
	try {
		return await action();
	} finally {
		void device.showProcessingOverlay(false);
	}
};
